import { HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { concat, Observable, of } from 'rxjs';
import { finalize, shareReplay, tap } from 'rxjs/operators';
import { SKIP_LOADER } from 'src/app/interceptors/loading.interceptor';
import { ApiService } from 'src/app/services/api.service';

// ============================================================================
// API payload shapes for the Communication Dashboard. Property names are
// camelCase to match ASP.NET Core's default JSON serialization of the
// CommDashboard* DTOs. Date fields arrive as ISO strings (UTC, no offset).
// ============================================================================

export type Period = 'week' | 'month';

export interface CommDashboardSummaryDto {
	emailRequests: number;
	emailDelivered: number;
	emailOpened: number;
	emailClicked: number;
	emailBounces: number;
	emailSpamReports: number;
	emailUnsubscribed: number;
	emailDeliveredPct: number;
	emailOpenedPct: number;
	emailClickedPct: number;
	emailBouncesPct: number;
	emailSpamPct: number;
	emailUnsubscribedPct: number;
	smsSent: number;
	smsDelivered: number;
	smsUndelivered: number;
	smsFailed: number;
	smsDeliveredPct: number;
	smsUndeliveredPct: number;
	smsFailedPct: number;
}

export interface CommActivityPointDto {
	activityDate: string;
	emailSent: number;
	emailDelivered: number;
	emailOpened: number;
	emailClicked: number;
	emailBounced: number;
	smsSent: number;
	smsDelivered: number;
	smsUndelivered: number;
	smsFailed: number;
}

export interface CommEmployerDto {
	employerID: string;
	employerName: string;
	deliveryTypeID: number;
	commPreference: string;
	dripStartDate: string | null;
	censusLoaded: number;
	campaignsSent: number;
	campaignsTotal: number;
	lastCampaign: string | null;
	nextCampaign: string | null;
	emailDeliveredPct: number | null;
	smsDeliveredPct: number | null;
	hasMissingFields: boolean;
	hasIssues: boolean;
	stage: 'build' | 'staged' | 'active';
}

export interface CommCampaignDto {
	campaignID: string;
	campaignName: string;
	type: 'Single' | 'Drip';
	isScheduled: boolean;
	scheduleStatus: 'sent' | 'scheduled' | 'overdue' | 'paused';
	sendDate: string | null;
	emailSent: number;
	emailDeliveredPct: number | null;
	openedPct: number | null;
	clickedPct: number | null;
	bouncedPct: number | null;
	droppedPct: number | null;
	spamPct: number | null;
	smsSent: number;
	smsDeliveredPct: number | null;
	smsFailPct: number | null;
	alertHighBounce: boolean;
	alertHighSpam: boolean;
	alertHighDrop: boolean;
	alertHighDeferral: boolean;
	alertLowDelivery: boolean;
	alertHighSmsFailure: boolean;
	alertLowSmsDelivery: boolean;
	alertEmptyCensus: boolean;
}

export interface CommAlertDto {
	type: 'error' | 'warning' | 'info';
	category: string;
	message: string;
}

export interface CommEmployerDetailDto {
	employer: CommEmployerDto;
	allTime: CommDashboardSummaryDto;
	campaigns: CommCampaignDto[];
	alerts: CommAlertDto[];
}

// One row of dbo.omCommAlertThreshold. This is the SINGLE source of truth for every
// metric color shown on the dashboard — the components evaluate each displayed rate
// against the matching (metricCode, channel) row instead of hardcoding any numbers.
export type ThresholdSeverity = 'warning' | 'error' | null;

export interface CommAlertThresholdDto {
	metricCode: string;   // BounceRate | SpamRate | DropRate | DeferralRate | DeliveryRate | SMSFailureRate
	channel: string;      // Email | SMS
	comparator: string;   // '>' or '<'
	thresholdPct: number;
	severity: string;     // Warning | Error
	isActive: boolean;
}

// How long a dashboard response is reused without asking the server. An older copy is still shown at
// once and refreshed in the background. 5 minutes matches the CampaignSenderJob cycle, so a fresher
// copy would rarely differ; the page's Refresh button skips the cache.
export const DASHBOARD_CACHE_TTL_MS = 5 * 60 * 1000;

interface CacheEntry<T> {
	value?: T;
	fetchedAt?: number;          // set once a response has arrived
	inflight?: Observable<T>;    // the request in progress, shared by every caller
}

export interface GridViewState {
	period: Period;
	sortColumn: string;
	sortDirection: 'asc' | 'desc';
}

@Injectable({ providedIn: 'root' })
export class CommDashboardService {

	// Active thresholds, cached for the service lifetime so the synchronous coloring helpers
	// below can run during template change-detection. Populated by getThresholds().
	private thresholds: CommAlertThresholdDto[] = [];

	// Responses by request URL. The service is app-wide, so this survives moving between the grid and
	// an employer; a full page reload empties it.
	private cache = new Map<string, CacheEntry<any>>();

	// The grid's period and sort, kept here so coming back from an employer shows the grid as it was left.
	public gridView: GridViewState = { period: 'week', sortColumn: 'name', sortDirection: 'asc' };

	constructor(private api: ApiService) {}

	// Every getter below follows the same cache rule (see cached()): a fresh copy completes at once with
	// no request; a stale copy is emitted at once and then the server's answer is emitted as well; with no
	// copy, or force = true, only the server's answer is emitted. Subscribers may therefore see two values.

	getSummary(period: Period, employerId?: string, force = false): Observable<CommDashboardSummaryDto> {
		return this.cached<CommDashboardSummaryDto>(this.summaryUrl(period, employerId), force);
	}

	getActivity(period: Period, employerId?: string, force = false): Observable<CommActivityPointDto[]> {
		return this.cached<CommActivityPointDto[]>(this.activityUrl(period, employerId), force);
	}

	getEmployers(force = false): Observable<CommEmployerDto[]> {
		return this.cached<CommEmployerDto[]>(`commdashboard/employers`, force);
	}

	getEmployerDetail(employerId: string, force = false): Observable<CommEmployerDetailDto> {
		return this.cached<CommEmployerDetailDto>(this.detailUrl(employerId), force);
	}

	// Loads the active alert thresholds into the coloring cache. Subscribe to it when a dashboard
	// page loads so the cells and stats render with table-driven colors.
	getThresholds(force = false): Observable<CommAlertThresholdDto[]> {
		return this.cached<CommAlertThresholdDto[]>(`commdashboard/thresholds`, force)
			.pipe(tap(list => (this.thresholds = list ?? [])));
	}

	/** When the oldest response the grid is showing was fetched, or null while any is missing. */
	gridUpdatedAt(period: Period): Date | null {
		return this.oldestFetch([`commdashboard/employers`, this.summaryUrl(period), this.activityUrl(period)]);
	}

	/** When the oldest response an employer's page is showing was fetched, or null while any is missing. */
	detailUpdatedAt(employerId: string, period: Period): Date | null {
		return this.oldestFetch([this.detailUrl(employerId), this.summaryUrl(period, employerId), this.activityUrl(period, employerId)]);
	}

	private cached<T>(url: string, force: boolean): Observable<T> {
		let entry: CacheEntry<T> = this.cache.get(url);
		if (!entry) {
			entry = {};
			this.cache.set(url, entry);
		}
		const hasCopy = entry.fetchedAt !== undefined;
		if (hasCopy && !force && Date.now() - entry.fetchedAt < DASHBOARD_CACHE_TTL_MS) {
			return of(entry.value);
		}
		if (!entry.inflight) {
			// With nothing on screen yet the app's spinner shows as usual; a refresh behind a copy already
			// on screen stays quiet. shareReplay lets the request finish (and fill the cache) even if the
			// page that asked is left before the answer arrives.
			const context = hasCopy ? new HttpContext().set(SKIP_LOADER, true) : null;
			entry.inflight = this.api.get<T>(url, null, context).pipe(
				tap(value => {
					entry.value = value;
					entry.fetchedAt = Date.now();
				}),
				finalize(() => (entry.inflight = undefined)),
				shareReplay(1)
			);
		}
		return hasCopy && !force ? concat(of(entry.value), entry.inflight) : entry.inflight;
	}

	private oldestFetch(urls: string[]): Date | null {
		const times = urls.map(u => this.cache.get(u)?.fetchedAt);
		return times.every(t => t !== undefined) ? new Date(Math.min(...times)) : null;
	}

	private summaryUrl(period: Period, employerId?: string): string {
		return `commdashboard/summary?${this.qs(period, employerId)}`;
	}

	private activityUrl(period: Period, employerId?: string): string {
		return `commdashboard/activity?${this.qs(period, employerId)}`;
	}

	private detailUrl(employerId: string): string {
		return `commdashboard/employers/${employerId}`;
	}

	// ── Table-driven metric coloring ────────────────────────────────────
	// Every displayed rate is colored ONLY by comparing it against its matching
	// omCommAlertThreshold row. No thresholds live in the components or templates.

	/**
	 * Returns the severity a value breaches for a given (metricCode, channel), or null when it
	 * is within tolerance / there is no active threshold / the value is missing. The table's PK
	 * is (metricCode, channel), so at most one row matches; its comparator + thresholdPct decide
	 * the breach and its severity decides the color.
	 */
	breachSeverity(value: number | null | undefined, metricCode: string, channel: string): ThresholdSeverity {
		if (value === null || value === undefined) {
			return null;
		}
		const t = this.thresholds.find(x => x.isActive && x.metricCode === metricCode && x.channel === channel);
		if (!t) {
			return null;
		}
		const breached = t.comparator === '>' ? value > t.thresholdPct : value < t.thresholdPct;
		if (!breached) {
			return null;
		}
		return (t.severity || '').toLowerCase() === 'error' ? 'error' : 'warning';
	}

	/** ngClass map for grid cells (employer grid + campaign table). */
	rateCellClass(value: number | null | undefined, metricCode: string, channel: string): { [cls: string]: boolean } {
		const sev = this.breachSeverity(value, metricCode, channel);
		return { 'rate-warn': sev === 'warning', 'rate-error': sev === 'error' };
	}

	/** ngClass map for the summary stat-card values. */
	statValueClass(value: number | null | undefined, metricCode: string, channel: string): { [cls: string]: boolean } {
		const sev = this.breachSeverity(value, metricCode, channel);
		return { 'warn': sev === 'warning', 'error': sev === 'error' };
	}

	private qs(period: Period, employerId?: string): string {
		const params = new URLSearchParams();
		params.set('period', period);
		if (employerId) {
			params.set('employerId', employerId);
		}
		return params.toString();
	}
}
